import { taskAPI } from '@/utils/api';
import {
  getDedupedCompositeResultsForGroup,
} from '@/utils/goodsCompositeSelection';
import {
  resolveGoodsTaskBillingNavForExport,
} from './goodsExportLinkedComposites';
import { resolveAiCopywritingTitlesByImageIds } from './goodsExportPreviewTitles';
import {
  getSourcingImageSpaceRecordId,
  getSourcingImageSpaceUrl,
  getSourcingProductPrimaryImage,
} from './sourcingProductDisplay';
import { setProductMeta, readMetaMap } from './platformListingProductMeta';

function trimText(value) {
  return String(value || '').trim();
}

function collectCompositeUrlsFromGoodsTask(task) {
  const forms = task?.goodsForms || task?.result?.goodsForms || [];
  const urls = [];
  (Array.isArray(forms) ? forms : []).forEach((form) => {
    const groupCount = form?.imageGroups?.length || 0;
    for (let groupIndex = 0; groupIndex < groupCount; groupIndex += 1) {
      const results = getDedupedCompositeResultsForGroup(form, groupIndex);
      results.forEach((result) => {
        const url = trimText(result?.compositeUrl || result?.url || result?.dataUrl);
        if (url) urls.push(url);
      });
    }
  });
  return [...new Set(urls)];
}

function collectTaskImageIds(task) {
  const ids = [
    ...(Array.isArray(task?.imageIds) ? task.imageIds : []),
    ...(Array.isArray(task?.result?.imageIds) ? task.result.imageIds : []),
  ].map(String).map((id) => id.trim()).filter(Boolean);
  return [...new Set(ids)];
}

function taskMentionsImageId(task, imageId) {
  const want = trimText(imageId);
  if (!want) return false;
  if (collectTaskImageIds(task).includes(want)) return true;
  const forms = task?.goodsForms || task?.result?.goodsForms || [];
  return (Array.isArray(forms) ? forms : []).some((form) => (
    (Array.isArray(form?.imageGroups) ? form.imageGroups : []).some((group) => (
      Object.values(group || {}).some((ref) => {
        if (ref == null) return false;
        if (typeof ref === 'string' || typeof ref === 'number') {
          return String(ref).trim() === want;
        }
        if (typeof ref === 'object') {
          return String(ref.id || ref.imageId || '').trim() === want;
        }
        return false;
      })
    ))
  ));
}

function isSuccessLike(status) {
  const s = String(status || '').toLowerCase();
  return s === 'success' || s === 'partial' || s === 'completed' || s === 'done' || s === 'processing';
}

/**
 * 素材采集 → 商品库：若同图已走过主图创作 / POD / AI文案，优先用加工成果覆盖展示。
 * 结果写入本地 product meta，供详情页/列表读取。
 *
 * @param {object[]} rows 采集条目
 * @returns {Promise<{ enrichedCount: number }>}
 */
export async function preferDownstreamAssetsForSourcingListingRows(rows = []) {
  const list = (Array.isArray(rows) ? rows : []).filter((row) => row?.id != null);
  if (list.length === 0) return { enrichedCount: 0 };

  let goodsTasks = [];
  try {
    const res = await taskAPI.getAll({ type: 'goods', pageSize: 100, summary: true });
    goodsTasks = (Array.isArray(res?.tasks) ? res.tasks : [])
      .filter((task) => isSuccessLike(task?.status));
  } catch (error) {
    console.warn('[listingPreferDownstream] 加载 goods 任务失败', error);
    return { enrichedCount: 0 };
  }

  const imageIds = list
    .map((row) => getSourcingImageSpaceRecordId(row))
    .filter(Boolean);
  const titleByImageId = imageIds.length > 0
    ? await resolveAiCopywritingTitlesByImageIds(imageIds, { allowImageIdScopedScan: true })
    : new Map();

  let enrichedCount = 0;
  for (const row of list) {
    const imageId = getSourcingImageSpaceRecordId(row);
    if (!imageId) continue;

    // 用户已手动改过标题/主图/详情图：不再被下游同步覆盖
    const existing = readMetaMap()[String(row.id)];
    if (existing && existing.preferredSource === 'manual_edit') {
      continue;
    }

    const related = goodsTasks.filter((task) => taskMentionsImageId(task, imageId));
    const goodsRelated = related.filter(
      (task) => resolveGoodsTaskBillingNavForExport(task) !== 'podProductDesign',
    );
    const podRelated = related.filter(
      (task) => resolveGoodsTaskBillingNavForExport(task) === 'podProductDesign',
    );

    const mainUrls = goodsRelated.flatMap((task) => collectCompositeUrlsFromGoodsTask(task));
    const detailUrls = podRelated.flatMap((task) => collectCompositeUrlsFromGoodsTask(task));
    const titleMeta = titleByImageId.get(imageId);
    const preferredTitle = trimText(
      (typeof titleMeta === 'string' ? titleMeta : titleMeta?.title) || '',
    );

    if (mainUrls.length === 0 && detailUrls.length === 0 && !preferredTitle) {
      continue;
    }

    const fallbackPrimary = getSourcingImageSpaceUrl(row) || getSourcingProductPrimaryImage(row);
    setProductMeta(row.id, {
      preferredMainImageUrls: mainUrls.length > 0 ? mainUrls : undefined,
      preferredDetailImageUrls: detailUrls.length > 0 ? detailUrls : undefined,
      preferredTitle: preferredTitle || undefined,
      preferredPrimaryImageUrl: mainUrls[0] || detailUrls[0] || fallbackPrimary || undefined,
      preferredSource: 'downstream_goods_pod_copywriting',
    });
    enrichedCount += 1;
  }

  return { enrichedCount };
}

/** 读取商品库条目上的展示覆盖（下游成果 / 手动编辑） */
export function getListingPreferredOverrides(itemId) {
  const id = String(itemId ?? '').trim();
  if (!id) return null;
  const row = readMetaMap()[id];
  if (!row || typeof row !== 'object') return null;
  const source = String(row.preferredSource || '').trim();
  if (source !== 'downstream_goods_pod_copywriting' && source !== 'manual_edit') {
    return null;
  }
  const hasMainOverride = Array.isArray(row.preferredMainImageUrls);
  const hasDetailOverride = Array.isArray(row.preferredDetailImageUrls);
  const hasPrintOverride = Array.isArray(row.preferredPrintImageUrls);
  const mainImageUrls = hasMainOverride
    ? row.preferredMainImageUrls.map(String).filter(Boolean)
    : [];
  const detailImageUrls = hasDetailOverride
    ? row.preferredDetailImageUrls.map(String).filter(Boolean)
    : [];
  const printImageUrls = hasPrintOverride
    ? row.preferredPrintImageUrls.map(String).filter(Boolean)
    : [];
  const printImageNames = Array.isArray(row.preferredPrintImageNames)
    ? row.preferredPrintImageNames.map((n) => String(n || '').trim()).filter(Boolean)
    : [];
  const mainImageNames = Array.isArray(row.preferredMainImageNames)
    ? row.preferredMainImageNames.map((n) => String(n || '').trim())
    : [];
  const detailImageNames = Array.isArray(row.preferredDetailImageNames)
    ? row.preferredDetailImageNames.map((n) => String(n || '').trim())
    : [];
  const colorLabel = trimText(row.preferredColorLabel);
  const colorCode = trimText(row.preferredColorCode);
  const title = trimText(row.preferredTitle);
  const primaryImageUrl = trimText(row.preferredPrimaryImageUrl);
  const hasColorSlotsOverride = Array.isArray(row.preferredColorSlots) && row.preferredColorSlots.length > 0;
  if (
    !title
    && !primaryImageUrl
    && mainImageUrls.length === 0
    && detailImageUrls.length === 0
    && printImageUrls.length === 0
    && printImageNames.length === 0
    && mainImageNames.length === 0
    && detailImageNames.length === 0
    && !colorLabel
    && !colorCode
    && !hasColorSlotsOverride
    && !hasMainOverride
    && !hasDetailOverride
    && !hasPrintOverride
  ) {
    return null;
  }
  return {
    mainImageUrls,
    detailImageUrls,
    printImageUrls,
    printImageNames,
    mainImageNames,
    detailImageNames,
    colorLabel,
    colorCode,
    title,
    primaryImageUrl,
    source,
    hasMainOverride,
    hasDetailOverride,
    hasPrintOverride,
    hasColorSlotsOverride,
  };
}

/** 供列表/详情展示：有下游成果时覆盖标题 */
export function resolveListingItemDisplayTitle(item) {
  const overrides = getListingPreferredOverrides(item?.id);
  if (overrides?.title) return overrides.title;
  return String(item?.title || '').trim() || '未命名商品';
}

/** 调试/测试：确认 meta 可读 */
export function hasListingPreferredOverrides(itemId) {
  return Boolean(getListingPreferredOverrides(itemId));
}
